import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { Link } from 'react-router-dom'
import CommentItem from '../../../../../components/customElements/CommentItem'
import InputField from '../../../../../components/customElements/InputField/InputField'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import {
  orderCheck,
  orderToClarify,
  removeOrders,
} from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../../utils/controllers/ModalController'
import { newRequestsSwitcherStore } from '../../../../RequestsManagement/components/NewRequestsTable/stores/new-requests-table.stores'
import { REQUEST_KEYS } from '../../../../SingleRequestPage/consts/request-keys'
import { REPORT_KEYS } from '../../../../SingleRequestReport/consts/request-report.keys'
import singleRequestStore from '../../../store/SingleRequestStore'
import { RequestType } from '../../../types/Request.type'
import { mapChipValue } from '../../../utils/mapChipValue'
import TypedAccordion from '../elements/TypedAccordion'
import { getFeatureState } from '../helpers/getDefaultDrawerState'
import requestDrawerStore from '../RequestDrawerStore'
import { titleSx } from '../styles/styles'

type DrawerContentDefaultProps = {
  onExit: () => void
  request: RequestType
}

export const DrawerContentDefault: React.FC<DrawerContentDefaultProps> = ({
  onExit,
  request,
}) => {
  const invalidate = useInvalidateQueries()
  const { getComments, comments } = singleRequestStore
  const { backRoute } = newRequestsSwitcherStore
  const navigate = useNavigate()
  const { setMode, withReportActions } = requestDrawerStore
  const [comment, setComment] = React.useState('')
  const intl = useIntl()
  const state = getFeatureState(request, backRoute)

  const isShownForAdmin =
    backRoute?.includes('/reports/') &&
    request.status === 13 &&
    request.reportStatus === 2

  const calrifyRequest = () => {
    openModalFn['progress-backdrop']?.()
    orderToClarify({ orderId: request.id, comment })
      .then(() => {
        setComment('')
        invalidate([
          'requests-table',
          REQUEST_KEYS.REQUEST,
          REPORT_KEYS.GET_REPORT,
          REPORT_KEYS.GET_REPORTS,
          REPORT_KEYS.GET_REPORT_TABLE,
        ])
        getComments(request.id)
      })
      .finally(() => {
        closeModalFn['progress-backdrop']?.()
      })
  }

  const deleteFromReport = () => {
    const action = async () => {
      await removeOrders({
        reportId: request.reportId,
        orderIdList: [request.id],
      })
        .then(() => {
          notifySuccess(
            `Заявка №${request.id} удалена из отчета  №${request.reportId}`,
          )
          onExit()
          backRoute?.includes('/reports/') && navigate(backRoute)
          invalidate([
            'requests-table',
            REQUEST_KEYS.REQUEST,
            REPORT_KEYS.GET_REPORT,
            REPORT_KEYS.GET_REPORTS,
            REPORT_KEYS.GET_REPORT_TABLE,
          ])
        })
        .finally(() => {
          closeModalFn['progress-backdrop']?.()
        })
    }
    // @ts-ignore
    openModalFn['transaction-confirmation']({
      text1: `Подтвердить удаление заявки №${request.id} из отчета №${request.reportId}?`,
      processText: `Выполняется удаление заявки №${request.id} из отчета №${request.reportId}`,
      action: action,
    })
  }

  const checkOrder = () => {
    openModalFn['progress-backdrop']?.()
    orderCheck({ orderId: request.id })
      .then(() => {
        invalidate([
          'requests-table',
          REQUEST_KEYS.REQUEST,
          REPORT_KEYS.GET_REPORT,
          REPORT_KEYS.GET_REPORTS,
          REPORT_KEYS.GET_REPORT_TABLE,
        ])
      })
      .finally(() => {
        closeModalFn['progress-backdrop']?.()
      })
  }

  const openFullComments = state.canPostComments

  return (
    <Stack>
      <TypedAccordion
        summary={'Статус заявки'}
        onEdit={state.canChangeStatus ? () => setMode('status') : undefined}
        disableEditIcon={!state.canChangeStatus}
      >
        {mapChipValue(request, 'status')}
      </TypedAccordion>
      <TypedAccordion
        summary={'Дилер'}
        onEdit={state.canChangeDealer ? () => setMode('dealer') : undefined}
        disableEditIcon={!state.canChangeDealer}
      >
        {request.dealer ? (
          mapChipValue(request, 'dealer')
        ) : (
          <Typography variant='body1'>Дилер не назначен</Typography>
        )}
      </TypedAccordion>
      <TypedAccordion
        summary={'Тип оплаты'}
        onEdit={
          state.canChangePaymentType ? () => setMode('payment-type') : undefined
        }
        disableEditIcon={!state.canChangePaymentType}
      >
        {state.isPaymentSelected ? (
          mapChipValue(request, 'financialSourceId')
        ) : (
          <Typography variant='body2'>Не выбран</Typography>
        )}
      </TypedAccordion>
      <TypedAccordion summary={'Номер и статус отчёта'}>
        {state.canSeeReportData && (
          <Stack>
            <Typography variant='body2' sx={titleSx}>
              номер и статус отчета
            </Typography>
            <Stack direction={'row'} justifyContent={'space-between'}>
              {mapChipValue(request, 'reportId')}
              <Link to={`/reports/${request.reportId}`} target='_blank'>
                <IconButton
                  disabled={state.disableGoToReportBnt}
                  sx={{ opacity: state.disableGoToReportBnt ? '.4' : 1 }}
                >
                  <img
                    src={getImageSrc('go-somewhere')}
                    alt='Перейти к отчету'
                  />
                </IconButton>
              </Link>
            </Stack>
          </Stack>
        )}
        {!state.canSeeReportData && (
          <Stack>
            <Typography variant='body1'>Не добавлена в отчет</Typography>
          </Stack>
        )}
      </TypedAccordion>
      <TypedAccordion summary={'Комментарии к заявке'}>
        <Stack spacing={'16px'}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} px={'16px'}>
              <Box
                component={'img'}
                src={
                  comments.length
                    ? getImageSrc('comment-message')
                    : getImageSrc('comment')
                }
              />
              <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                Комментарии ({comments.length})
              </Typography>
            </Stack>
            {openFullComments && (
              <IconButton onClick={() => setMode('comment')}>
                <img
                  src={getImageSrc('go-somewhere')}
                  alt='Перейти к комментариям'
                />
              </IconButton>
            )}
          </Stack>
          {!!comments.length && (
            <CommentItem comment={comments[comments.length - 1]} />
          )}
        </Stack>
      </TypedAccordion>
      {withReportActions && (
        <Stack px={2} spacing={'8px'} paddingBottom={'40px'}>
          {state.showCommentField && (
            <Stack sx={{ paddingY: '16px' }}>
              <InputField
                multiline
                value={comment}
                onChange={(e: React.ChangeEvent<any>) =>
                  setComment(e.target.value)
                }
                rows={4}
                fullWidth
                territorySelect={false}
                placeholder={intl.formatMessage({ id: 'Введите комментарий' })}
              />
            </Stack>
          )}
          {isShownForAdmin && request.dataCheckingStatus === 2 && (
            <Button
              color={'primary'}
              variant={'contained'}
              onClick={checkOrder}
              fullWidth
            >
              <FormattedMessage id={'Принять заявку'} />
            </Button>
          )}
          {isShownForAdmin && request.dataCheckingStatus !== 2 && (
            <Button
              color={'primary'}
              variant={'contained'}
              onClick={calrifyRequest}
              fullWidth
              disabled={!comment}
            >
              <FormattedMessage id={'Уточнить данные'} />
            </Button>
          )}
          {isShownForAdmin && (
            <Button
              color={'primary'}
              variant={'contained'}
              onClick={deleteFromReport}
              fullWidth
              sx={{ margin: '0 0 24px 0' }}
            >
              <FormattedMessage id={'Убрать заявку'} />
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default observer(DrawerContentDefault)
